import React, { useState } from "react";
import { Download, Upload, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";

function ReportTable({ report }) {
  if (!report.length) return <p className="text-sm text-neutral-500" data-testid="merge-nothing-new">Nothing new to add — this app already has every entry from the file.</p>;
  return (
    <table className="w-full text-sm" data-testid="merge-report-table">
      <thead><tr className="text-left text-xs uppercase tracking-wider text-neutral-500"><th className="py-2">Section</th><th>New entries</th><th>Skipped (already here)</th></tr></thead>
      <tbody>
        {report.map((r) => (
          <tr key={r.collection} className="border-t border-neutral-200 align-top">
            <td className="py-2 font-medium">{r.collection.replace(/_/g, " ")}
              {r.updated > 0 && <div className="text-xs text-blue-700 font-normal mt-1">{r.updated} existing logins will get the password from the file</div>}
              {r.possible_duplicates.length > 0 && (
                <div className="text-xs text-amber-700 font-normal mt-1">Same name already exists: {r.possible_duplicates.join(", ")}</div>
              )}
            </td>
            <td className="py-2">{r.new}</td>
            <td className="py-2">{r.skipped_conflict}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ExportCard() {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const resp = await api.get("/admin/data-export", { responseType: "blob" });
      const name = (resp.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || "lpg-backup.json.gz";
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Backup file downloaded");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="border border-neutral-200 bg-white p-6" data-testid="export-card">
      <h2 className="text-base md:text-lg font-semibold">1. Download all data</h2>
      <p className="text-sm text-neutral-600 mt-1 mb-4">Saves every party, entry, payment, stock record and login from this app into one backup file.</p>
      <Button onClick={download} disabled={busy} data-testid="export-download-btn">
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}Download backup file
      </Button>
    </div>
  );
}

function MergeCard() {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [syncLogins, setSyncLogins] = useState(true);
  const send = async (apply) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/data-merge?apply=${apply}&sync_logins=${syncLogins}`, fd, { timeout: 300000 });
      setResult(data);
      if (apply) toast.success(`Added ${data.total_new} entries`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="border border-neutral-200 bg-white p-6" data-testid="merge-card">
      <h2 className="text-base md:text-lg font-semibold">2. Add entries from a backup file</h2>
      <p className="text-sm text-neutral-600 mt-1 mb-4">Only adds entries this app doesn't have yet. Nothing here is deleted or changed.</p>
      <input type="file" accept=".gz,.json" data-testid="merge-file-input" className="block text-sm mb-4"
        onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }} />
      <label className="flex items-start gap-2 text-sm mb-4 cursor-pointer">
        <input type="checkbox" className="mt-1" checked={syncLogins} data-testid="merge-sync-logins-checkbox"
          onChange={(e) => { setSyncLogins(e.target.checked); setResult(null); }} />
        <span>Copy all logins from the file — passwords, names and roles in this app become the same as in the file</span>
      </label>
      <div className="flex flex-wrap gap-3">
        <Button variant="outline" disabled={!file || busy} onClick={() => send(false)} data-testid="merge-check-btn">
          {busy && !result ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}Check what will be added
        </Button>
        {result && !result.applied && (result.total_new > 0 || result.report.some((r) => r.updated > 0)) && (
          <Button disabled={busy} onClick={() => send(true)} data-testid="merge-apply-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}Add {result.total_new + (result.report.find((r) => r.updated)?.updated || 0)} changes now
          </Button>
        )}
      </div>
      {result && (
        <div className="mt-6" data-testid="merge-result">
          <p className={`text-sm font-medium mb-3 ${result.applied ? "text-green-700" : "text-neutral-800"}`} data-testid="merge-result-status">
            {result.applied ? `Done — ${result.total_new + (result.report.find((r) => r.updated)?.updated || 0)} changes added.` : `Preview — ${result.total_new} new entries found (backup from ${result.exported_at?.slice(0, 16).replace("T", " ")}).`}
          </p>
          <ReportTable report={result.report} />
        </div>
      )}
    </div>
  );
}

export default function DataTransfer() {
  return (
    <div className="space-y-6 max-w-3xl" data-testid="data-transfer-page">
      <div className="flex gap-3 items-start bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900">
        <AlertTriangle className="w-5 h-5 shrink-0" />
        <span>To move preview data to the live app: download the backup on the <b>preview</b> link, then open the <b>live</b> link, log in as admin and upload it in step 2.</span>
      </div>
      <ExportCard />
      <MergeCard />
    </div>
  );
}
